import styled from '@emotion/styled';
import {
    BodyTextMedium,
    Button,
    ButtonGroup,
    Form,
    FormInputPassword,
    HeadingText50,
    PasswordValidationElement,
    pxToRem,
    useForm,
} from '@modernhealth/vital-components';
import clevertap from 'clevertap-web-sdk';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import momentTimezone from 'moment-timezone';

import { navigationService, userAuthenticator } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import logoImg from 'src/public/img/mh-logo-text.svg';
import {
    containsLowercaseLetter,
    containsNumber,
    containsSpecialCharacter,
    containsUppercaseLetter,
} from 'src/shared/helpers/characterContainValidators';
import { getLanguageNameByI18nLanguage } from 'src/shared/helpers/getPreferredLanguage';
import { config } from 'src/config/config';

import { PasswordFormPageTestId } from './testIds';
import { httpClient } from 'src/config/http';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

interface PasswordFormParams {
    token: string;
    companyCode: string;
    companyName: string;
    terms: Record<string, string>;
    country: string;
    birthDate: string;
    firstName: string;
    lastName: string;
    email: string;
    registrationToken: string | undefined;
    uidb64: string;
    languagePreference: string;
    acceptedContractVersions: string[];
}

const BrandImage = styled.img({
    marginLeft: pxToRem(32),
    marginTop: pxToRem(20),
    width: '15em',
    cursor: 'pointer',
    marginRight: pxToRem(12),
});

const ValidationContainer = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    paddingBottom: pxToRem(16),
    gap: pxToRem(12),
    width: '100%',
});

const FormContainer = styled.div({
    maxWidth: pxToRem(500),
    width: '100%',
});

const Container = styled.div({
    display: 'flex',
    alignItems: 'start',
    paddingTop: pxToRem(112),
    paddingBottom: pxToRem(14),
    justifyContent: 'center',
    flexGrow: 1,
});

type PasswordFormData = { password: string };

interface RegisterResponse {
    email: string;
}
interface RegisterParams {
    birth_date: string;
    token: string;
    country: string;
    terms: Record<string, string>;
    password: string;
    firstName: string;
    lastName: string;
    companyCode: string;
    email: string;
    language_preferences: string[];
}

export const PasswordFormPage: React.FC = () => {
    const [wasPasswordEdited, setWasPasswordEdited] = useState(false);
    const location = useLocation<PasswordFormParams>();
    const registrationToken =
        location.state?.registrationToken || location.state?.uidb64 || undefined;
    const uidb64 = location.state?.uidb64;
    const {
        token,
        companyCode,
        companyName,
        birthDate,
        country,
        terms,
        email,
        firstName,
        lastName,
        languagePreference,
        acceptedContractVersions,
    } = location.state || {};

    const formSchema = Yup.object().shape({
        password: Yup.string().test(
            'password-is-valid',
            '',
            (value: string) =>
                containsSpecialCharacter(value) &&
                containsUppercaseLetter(value) &&
                containsLowercaseLetter(value) &&
                containsNumber(value) &&
                value.length >= 12,
        ),
    });
    const passwordFormDefinition = useForm<PasswordFormData>({
        resolver: yupResolver(formSchema),
    });

    useEffect(() => {
        setWasPasswordEdited(passwordFormDefinition.formState.isDirty);
    }, [passwordFormDefinition.formState.isDirty]);

    const password = passwordFormDefinition.watch('password', '');

    const { t, i18n } = useTranslation();

    const dispatch: Dispatch = useDispatch();

    const goBack = () => {
        navigationService.navigate('/consent-form-page', {
            token,
            companyCode,
            companyName,
            birthDate,
            firstName,
            lastName,
            email,
            uidb64,
            languagePreference,
            country,
            acceptedContractVersions,
        });
    };

    const submitPassword = async (data: PasswordFormData) => {
        const dateOfBirth = birthDate;
        const localTimezone = momentTimezone.tz.guess() || null;

        /*
                languagePreferences are sent here to avoid issues with changing language back to en-US for other supported languages
                TODO: this part can be deleted once the issue is resolved on the BE
            */
        const languagePreferences = [
            languagePreference ?? getLanguageNameByI18nLanguage(i18n.language),
        ];
        const platform = 'WebApp';
        const tokenBody = {
            birth_date: birthDate,
            dateOfBirth,
            token,
            country,
            terms,
            password: data.password,
            email,
            firstName,
            lastName,
            companyCode,
            registrationToken,
            language_preferences: languagePreferences,
            localTimezone,
            platform,
            acceptedContractVersions: acceptedContractVersions ?? [],
            accepted_contract_versions: acceptedContractVersions ?? [],
        };
        let response;
        let failed = false;
        if (token) {
            try {
                response = await httpClient.POST_UNAUTHENTICATED<RegisterParams, RegisterResponse>(
                    'accounts/register/',
                    tokenBody,
                );
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                failed = true;
                const detail = err?.body?.detail;
                switch (detail) {
                    case 'Token expired':
                        navigationService.navigate('/invalid-registration-token', {
                            token,
                        });
                        break;
                    default:
                        break;
                }
            }
        } else {
            await userAuthenticator.createAccount({
                ...tokenBody,
                clevertapId: clevertap.getCleverTapID(),
                company: companyCode?.toLowerCase(),
                country,
                registrationToken,
                languagePreferences,
            });
        }

        if (!failed) {
            await dispatch.authentication
                .login({
                    email: response ? response.email : email,
                    password: data.password,
                })
                .then(() => {
                    navigationService.navigate('/preferred-email');
                });
        }
    };

    return (
        <>
            <BrandImage
                src={logoImg}
                onClick={() => {
                    window.location.href = config.modernHealthURL;
                }}
            />
            <Container>
                <FormContainer>
                    <HeadingText50>{t('auth:password_form_page.title')}</HeadingText50>

                    <BodyTextMedium marginTop={16} marginBottom={24}>
                        {t('auth:password_form_page.description')}
                    </BodyTextMedium>

                    <Form onSubmit={submitPassword} definition={passwordFormDefinition}>
                        <FormInputPassword
                            trim
                            name="password"
                            label={t('auth:password_form_page.label.password')}
                        />
                        <ValidationContainer>
                            <PasswordValidationElement
                                testID={PasswordFormPageTestId.LengthValidation}
                                wasPasswordEdited={wasPasswordEdited}
                                isValid={password.trim().length >= 12}
                                text={t('auth:password_form_page.requirements.min_characters')}
                            />
                            <PasswordValidationElement
                                testID={PasswordFormPageTestId.SpecialCharacterValidation}
                                wasPasswordEdited={wasPasswordEdited}
                                isValid={containsSpecialCharacter(password)}
                                text={t(
                                    'auth:password_form_page.password.requirements.min_especial_characters',
                                )}
                            />
                            <PasswordValidationElement
                                testID={PasswordFormPageTestId.NumberValidation}
                                wasPasswordEdited={wasPasswordEdited}
                                isValid={containsNumber(password)}
                                text={t(
                                    'auth:password_form_page.password.requirements.min_numeric_characters',
                                )}
                            />
                            <PasswordValidationElement
                                testID={PasswordFormPageTestId.UppercaseValidation}
                                wasPasswordEdited={wasPasswordEdited}
                                isValid={containsUppercaseLetter(password)}
                                text={t(
                                    'auth:password_form_page.password.requirements.min_uppercase_characters',
                                )}
                            />
                            <PasswordValidationElement
                                testID={PasswordFormPageTestId.LowercaseValidation}
                                wasPasswordEdited={wasPasswordEdited}
                                isValid={containsLowercaseLetter(password)}
                                text={t(
                                    'auth:password_form_page.password.requirements.min_lowercase_characters',
                                )}
                            />
                        </ValidationContainer>
                        <ButtonGroup>
                            <Button onClick={goBack} variant="secondary" size="large">
                                {t('auth:login_form.back')}
                            </Button>
                            <Button
                                type="submit"
                                size="large"
                                disabled={!passwordFormDefinition.formState.isValid}
                                busy={passwordFormDefinition.formState.isSubmitting}
                            >
                                {t('auth:login_form.continue')}
                            </Button>
                        </ButtonGroup>
                    </Form>
                </FormContainer>
            </Container>
        </>
    );
};
